/**
 * Public pre-registration page (/inscripcion/:code) — no login.
 * Ministers identify by CI / ADB code / name and get their data prefilled;
 * delegates and (when the event allows it) guests fill the form by hand.
 * Field set follows the official "Formulario de Registro"; SIMPLE events show
 * a reduced version.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, Loader2, MapPin, UserCheck, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { calculatePrice } from '@/Services/pricingEngine.service';
import { formatPrice, parseLocalDate } from '@/models/event';
import { getEventFlyerUrl, getEventPaymentQrUrl } from '@/Services/events.service';
import {
  getMinisterPrefill,
  getPublicEvent,
  submitPreregistration,
  uploadRegistrationDocument,
} from '../publicRegistrationRepository';
import { MinisterLookup } from '../components/MinisterLookup';
import { DocumentUploadField } from '../components/DocumentUploadField';
import { PublicShell } from '../components/PublicShell';
import { PaymentQrCard } from '../components/PaymentQrCard';
import { SignaturePad } from '../components/SignaturePad';
import {
  DEFAULT_DECLARATION,
  type DocumentKind,
  type MinisterLookupResult,
  type PublicEvent,
  type RegistrationFormValues,
  type RegistrationKind,
} from '../types';

const EMPTY_VALUES: RegistrationFormValues = {
  kind: 'MINISTER',
  minister_id: null,
  full_name: '',
  dni: '',
  district_id: '',
  region_id: '',
  degree_id: '',
  position: '',
  office_phone: '',
  cell_phone: '',
  email: '',
  address: '',
  address_city: '',
  address_zone: '',
  birth_date: '',
  represented_minister_id: null,
  represented_name: '',
  represented_ci: '',
  institution_note: '',
};

function formatEventDates(event: PublicEvent) {
  const fmt = (v: string) =>
    parseLocalDate(v)?.toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' }) ?? v;
  return event.start_date === event.end_date ? fmt(event.start_date) : `${fmt(event.start_date)} – ${fmt(event.end_date)}`;
}

export function PublicRegistrationPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['public_event', code],
    queryFn: () => getPublicEvent(code as string),
    enabled: !!code,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <PublicShell>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertTriangle className="size-10 text-muted-foreground" />
          <p className="text-muted-foreground">Evento no encontrado o no disponible.</p>
          <p className="text-sm text-muted-foreground">Verifique el enlace que le compartieron.</p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <EventHeader event={event} />
      {event.is_open ? (
        <RegistrationForm event={event} onDone={(token) => navigate(`/p/${token}?nuevo=1`)} />
      ) : (
        <Alert>
          <AlertDescription>Las inscripciones para este evento están cerradas.</AlertDescription>
        </Alert>
      )}
    </PublicShell>
  );
}

function EventHeader({ event }: { event: PublicEvent }) {
  const flyerUrl = getEventFlyerUrl(event.flyer_filename);
  return (
    <Card>
      {flyerUrl && <img src={flyerUrl} alt={event.name} className="max-h-72 w-full rounded-t-lg object-cover" />}
      <CardHeader>
        <CardTitle className="text-xl">{event.name}</CardTitle>
        <div className="space-y-1 text-sm text-muted-foreground">
          <p className="flex items-center gap-2">
            <CalendarDays className="size-4" /> {formatEventDates(event)}
          </p>
          {event.location && (
            <p className="flex items-center gap-2">
              <MapPin className="size-4" /> {event.location}
            </p>
          )}
          {event.registration_deadline && (
            <p className="text-xs">
              Inscripciones hasta el {new Date(event.registration_deadline).toLocaleString('es-BO')}
            </p>
          )}
        </div>
      </CardHeader>
      {event.description && (
        <CardContent className="whitespace-pre-wrap text-sm text-muted-foreground">{event.description}</CardContent>
      )}
    </Card>
  );
}

// =====================================================
// Form
// =====================================================

function RegistrationForm({ event, onDone }: { event: PublicEvent; onDone: (token: string) => void }) {
  const isFull = event.registration_form_type === 'FULL_CONVENTION';
  const paymentQrUrl = getEventPaymentQrUrl(event.payment_qr_filename);
  const isDigitalSignature = event.signature_mode === 'DIGITAL';

  const [values, setValues] = useState<RegistrationFormValues>(EMPTY_VALUES);
  const [documents, setDocuments] = useState<Partial<Record<DocumentKind, string>>>({});
  const [signature, setSignature] = useState<Blob | null>(null);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  // Minister identification
  const [pendingMinister, setPendingMinister] = useState<MinisterLookupResult | null>(null);
  const [ciConfirm, setCiConfirm] = useState('');
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [identified, setIdentified] = useState<{ name: string; code: string } | null>(null);

  // Delegate
  const [representedLabel, setRepresentedLabel] = useState<string | null>(null);
  const [manualRepresented, setManualRepresented] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const set = <K extends keyof RegistrationFormValues>(key: K, value: RegistrationFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const kind = values.kind;
  const showDetails = kind !== 'MINISTER' || identified !== null;

  const kindOptions = [
    { value: 'MINISTER' as RegistrationKind, label: 'Ministro' },
    { value: 'DELEGATE' as RegistrationKind, label: 'Delegado' },
    ...(event.allow_public_guests ? [{ value: 'GUEST' as RegistrationKind, label: 'Invitado' }] : []),
  ];

  const changeKind = (next: RegistrationKind) => {
    setValues({ ...EMPTY_VALUES, kind: next });
    setDocuments({});
    setIdentified(null);
    setPendingMinister(null);
    setCiConfirm('');
    setRepresentedLabel(null);
    setManualRepresented(false);
    setFormError(null);
  };

  const loadPrefill = async (ministerId: string, dni: string) => {
    setPrefillLoading(true);
    setFormError(null);
    try {
      const m = await getMinisterPrefill(event.id, ministerId, dni);
      if (m.already_registered) {
        setFormError('Este ministro ya está inscrito en el evento.');
        return;
      }
      setValues((prev) => ({
        ...prev,
        minister_id: m.id,
        full_name: m.full_name ?? '',
        dni: m.dni,
        district_id: m.district_id ?? '',
        region_id: m.region_id ?? '',
        degree_id: m.degree_id ?? '',
        position: m.position ?? '',
        office_phone: m.office_phone ?? '',
        cell_phone: m.cell_phone ?? '',
        email: m.email ?? '',
        address: m.address ?? '',
        address_city: m.address_city ?? '',
        address_zone: m.address_zone ?? '',
        birth_date: m.birth_date ?? '',
      }));
      setIdentified({ name: m.full_name ?? '', code: m.adb_code });
      setPendingMinister(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo verificar el CI');
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleMinisterSelected = (m: MinisterLookupResult, query: string) => {
    // Searched by CI → the query already proves the CI, prefill directly
    if (m.matched_by_ci) {
      void loadPrefill(m.id, query);
    } else {
      setPendingMinister(m);
      setCiConfirm('');
    }
  };

  const resetIdentification = () => {
    setValues({ ...EMPTY_VALUES, kind: 'MINISTER' });
    setIdentified(null);
    setPendingMinister(null);
  };

  const regions = useMemo(
    () => (values.district_id ? event.regions.filter((r) => r.district_id === values.district_id) : []),
    [event.regions, values.district_id],
  );

  // Price preview — re-checked by an administrator when verifying the registration
  const price = useMemo(() => {
    const startDate = parseLocalDate(event.start_date) ?? new Date();
    return calculatePrice(
      {
        event_id: event.id,
        base_price: event.base_price,
        participant_data:
          kind === 'MINISTER'
            ? {
                participant_type: kind,
                minister_id: values.minister_id ?? undefined,
                minister_degree: values.degree_id || undefined,
                minister_district: values.district_id || undefined,
                minister_region: values.region_id || undefined,
                minister_role: values.position || undefined,
                minister_birth_date: values.birth_date ? parseLocalDate(values.birth_date) : undefined,
                registration_date: new Date(),
                event_start_date: startDate,
              }
            : { participant_type: kind, registration_date: new Date(), event_start_date: startDate },
      },
      event.pricing_rules,
    );
  }, [event, kind, values]);

  // Rules the public form can't fully evaluate (queue position, authority positions)
  const dependsOnReview = event.pricing_rules.some(
    (r) => r.is_active && r.conditions.some((c) => c.type === 'PARTICIPANT_COUNT' || c.type === 'ROLE'),
  );

  const requiredDocs: { kind: DocumentKind; label: string; hint?: string }[] =
    kind === 'DELEGATE'
      ? [
          {
            kind: 'delegation_letter',
            label: 'Carta de delegación',
            hint: 'Carta firmada por el pastor, líder o autoridad a quien representa.',
          },
        ]
      : [
          { kind: 'ci_front', label: 'Cédula de identidad — anverso' },
          { kind: 'ci_back', label: 'Cédula de identidad — reverso' },
        ];

  const missing: string[] = [];
  if (kind === 'MINISTER' && !identified) missing.push('Identifíquese como ministro');
  if (showDetails) {
    if (values.full_name.trim().length < 3) missing.push('Nombre completo');
    if (!values.dni.trim()) missing.push('Nº de CI');
    if (!values.cell_phone.trim()) missing.push('Celular');
    // Ministers' district comes from their record (not editable); only guests choose one
    if (kind === 'GUEST' && !values.district_id) missing.push('Distrito');
    if (kind === 'DELEGATE' && !values.represented_minister_id && !values.represented_name.trim()) {
      missing.push('A quién representa');
    }
    requiredDocs.forEach((d) => !documents[d.kind] && missing.push(d.label));
    if (!documents.voucher) missing.push('Comprobante de pago');
    if (!declarationAccepted) missing.push('Aceptar la declaración');
    if (isDigitalSignature && !signature) missing.push('Firma');
  }

  const handleSubmit = async () => {
    if (missing.length > 0) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const docs = { ...documents };
      if (isDigitalSignature && signature) {
        docs.signature = await uploadRegistrationDocument(event.id, 'signature', signature);
      }
      const { token } = await submitPreregistration({
        ...values,
        event_id: event.id,
        documents: docs,
        declaration_accepted: declarationAccepted,
        user_agent: navigator.userAgent,
        calculated_price: price.final_price,
        applied_pricing_rule_id: price.applied_rule?.id ?? null,
      });
      onDone(token);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo completar la inscripción');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 1. Who */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. ¿Cómo se inscribe?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <SegmentedControl options={kindOptions} value={kind} onChange={changeKind} className="w-full" />

          {kind === 'MINISTER' && !identified && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Busque sus datos por número de CI, código de ministro o nombre.
              </p>
              <MinisterLookup eventId={event.id} onSelect={handleMinisterSelected} blockRegistered />
              {pendingMinister && (
                <div className="space-y-2 rounded-md border bg-muted/50 p-3">
                  <p className="text-sm">
                    Para continuar como <strong>{pendingMinister.full_name}</strong>, confirme su número de CI:
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={ciConfirm}
                      onChange={(e) => setCiConfirm(e.target.value)}
                      placeholder="Nº de CI (solo el número, ej. 1234567)"
                      inputMode="numeric"
                    />
                    <Button
                      type="button"
                      onClick={() => loadPrefill(pendingMinister.id, ciConfirm)}
                      disabled={!ciConfirm.trim() || prefillLoading}
                    >
                      {prefillLoading ? <Loader2 className="size-4 animate-spin" /> : 'Continuar'}
                    </Button>
                  </div>
                </div>
              )}
              {prefillLoading && !pendingMinister && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Cargando sus datos...
                </p>
              )}
            </div>
          )}

          {kind === 'MINISTER' && identified && (
            <div className="flex items-center justify-between rounded-md border bg-green-50 p-3 dark:bg-green-950/30">
              <span className="flex items-center gap-2 text-sm">
                <UserCheck className="size-4 text-green-600" />
                <span>
                  <strong>{identified.name}</strong>
                  <span className="block text-xs text-muted-foreground">Registro ADB {identified.code}</span>
                </span>
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={resetIdentification}>
                Cambiar
              </Button>
            </div>
          )}

          {kind === 'DELEGATE' && (
            <div className="space-y-3">
              <Label>¿A quién representa?</Label>
              {representedLabel ? (
                <div className="flex items-center justify-between rounded-md border p-3 text-sm">
                  <span>{representedLabel}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setRepresentedLabel(null);
                      set('represented_minister_id', null);
                    }}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ) : manualRepresented ? (
                <div className="space-y-3">
                  <Field label="Nombre de la persona, iglesia o institución que representa" required>
                    <Input value={values.represented_name} onChange={(e) => set('represented_name', e.target.value)} />
                  </Field>
                  <Field label="Nº de CI del pastor / representante legal">
                    <Input value={values.represented_ci} onChange={(e) => set('represented_ci', e.target.value)} />
                  </Field>
                  <Field label="Detalle (ej. Exploradores — Región La Paz)">
                    <Input value={values.institution_note} onChange={(e) => set('institution_note', e.target.value)} />
                  </Field>
                  <Button type="button" variant="link" className="h-auto p-0" onClick={() => setManualRepresented(false)}>
                    Buscar ministro en su lugar
                  </Button>
                </div>
              ) : (
                <>
                  <MinisterLookup
                    eventId={event.id}
                    placeholder="Nombre, CI (1234567) o código (14-2429) del ministro que representa"
                    onSelect={(m) => {
                      set('represented_minister_id', m.id);
                      setRepresentedLabel(`${m.full_name} (${m.adb_code})`);
                    }}
                  />
                  <Button type="button" variant="link" className="h-auto p-0" onClick={() => setManualRepresented(true)}>
                    Represento a una iglesia, institución u otra persona
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showDetails && (
        <>
          {/* 2. Data */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Datos personales</CardTitle>
              {kind === 'MINISTER' && (
                <p className="text-xs text-muted-foreground">
                  Verifique sus datos. Distrito, región, grado y cargo provienen de su registro ministerial y no se
                  pueden modificar aquí; si hay un error, comuníquese con la comisión organizadora.
                  Los demás cambios serán revisados por la administración.
                </p>
              )}
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Nombre completo" required className="sm:col-span-2">
                <Input value={values.full_name} onChange={(e) => set('full_name', e.target.value)} />
              </Field>
              <Field label="Nº Cédula de identidad" required>
                <Input
                  value={values.dni}
                  onChange={(e) => set('dni', e.target.value)}
                  disabled={kind === 'MINISTER'}
                  inputMode="numeric"
                />
              </Field>
              {kind === 'MINISTER' && (
                <>
                  <Field label="Distrito">
                    <ReadOnlyValue value={nameOf(event.districts, values.district_id)} />
                  </Field>
                  <Field label="Región">
                    <ReadOnlyValue value={nameOf(event.regions, values.region_id)} />
                  </Field>
                </>
              )}

              {kind === 'GUEST' && (
                <>
                  <Field label="Distrito" required>
                    <Select
                      value={values.district_id || undefined}
                      onValueChange={(v) => setValues((p) => ({ ...p, district_id: v, region_id: '' }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione" />
                      </SelectTrigger>
                      <SelectContent>
                        {event.districts.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Región">
                    <Select
                      value={values.region_id || undefined}
                      onValueChange={(v) => set('region_id', v)}
                      disabled={!values.district_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={values.district_id ? 'Seleccione' : 'Elija un distrito'} />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </>
              )}

              {kind === 'MINISTER' && isFull && (
                <Field label="Grado ministerial">
                  <ReadOnlyValue value={nameOf(event.degrees, values.degree_id)} />
                </Field>
              )}

              {isFull && (
                <Field label="Cargo">
                  {kind === 'MINISTER' ? (
                    <ReadOnlyValue value={values.position} />
                  ) : (
                    <Input value={values.position} onChange={(e) => set('position', e.target.value)} />
                  )}
                </Field>
              )}

              {kind === 'MINISTER' && (
                <Field label="Fecha de nacimiento">
                  <Input type="date" value={values.birth_date} onChange={(e) => set('birth_date', e.target.value)} />
                </Field>
              )}

              <Field label="Celular" required>
                <Input
                  type="tel"
                  value={values.cell_phone}
                  onChange={(e) => set('cell_phone', e.target.value)}
                  inputMode="tel"
                />
              </Field>
              {isFull && (
                <Field label="Teléfono de oficina">
                  <Input type="tel" value={values.office_phone} onChange={(e) => set('office_phone', e.target.value)} />
                </Field>
              )}
              <Field label="E-mail">
                <Input type="email" value={values.email} onChange={(e) => set('email', e.target.value)} placeholder="Opcional" />
              </Field>
              {isFull && (
                <Field label="Dirección para envío de correspondencia" className="sm:col-span-2">
                  <Input value={values.address} onChange={(e) => set('address', e.target.value)} />
                </Field>
              )}
            </CardContent>
          </Card>

          {/* 3. Payment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">3. Pago de inscripción</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground">Monto a pagar</p>
                <p className="text-2xl font-bold">{formatPrice(price.final_price)}</p>
                {price.applied_rule && (
                  <p className="text-xs text-muted-foreground">
                    {price.applied_rule.name} (precio base {formatPrice(event.base_price)})
                  </p>
                )}
                {dependsOnReview && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Algunos descuentos dependen del orden de inscripción o del cargo; el monto final se confirma al
                    verificar tu inscripción.
                  </p>
                )}
              </div>
              {event.payment_instructions && (
                <p className="whitespace-pre-wrap text-sm">{event.payment_instructions}</p>
              )}
              {paymentQrUrl && <PaymentQrCard url={paymentQrUrl} fileLabel={event.code} />}
              <DocumentUploadField
                eventId={event.id}
                kind="voucher"
                label="Comprobante de pago"
                hint="Foto o captura del depósito / transferencia."
                value={documents.voucher}
                onChange={(key) => setDocuments((d) => ({ ...d, voucher: key }))}
              />
            </CardContent>
          </Card>

          {/* 4. Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">4. Documentos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {requiredDocs.map((doc) => (
                <DocumentUploadField
                  key={doc.kind}
                  eventId={event.id}
                  kind={doc.kind}
                  label={doc.label}
                  hint={doc.hint}
                  value={documents[doc.kind]}
                  onChange={(key) => setDocuments((d) => ({ ...d, [doc.kind]: key }))}
                />
              ))}
            </CardContent>
          </Card>

          {/* 5. Declaration + signature */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">5. Declaración</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm italic text-muted-foreground">
                {event.registration_declaration || DEFAULT_DECLARATION}
              </p>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  checked={declarationAccepted}
                  onCheckedChange={(c) => setDeclarationAccepted(c === true)}
                  className="mt-0.5"
                />
                Acepto la declaración y confirmo que los datos proporcionados son verídicos.
              </label>
              {isDigitalSignature ? (
                <div className="space-y-2">
                  <Label>Firma</Label>
                  <SignaturePad onChange={setSignature} disabled={submitting} />
                  <p className="text-xs text-muted-foreground">
                    Dibuje su firma con el dedo o el mouse. Es obligatoria para enviar la inscripción.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Firmará el formulario impreso al recoger su credencial en el evento.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {formError && (
        <Alert variant="destructive">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}

      {showDetails && (
        <div className="space-y-2">
          {missing.length > 0 && (
            <p className="text-xs text-muted-foreground">Falta completar: {missing.join(', ')}.</p>
          )}
          <Button className="w-full" size="lg" onClick={handleSubmit} disabled={missing.length > 0 || submitting}>
            {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Enviar inscripción
          </Button>
        </div>
      )}
    </div>
  );
}

const nameOf = (list: { id: string; name: string }[], id: string) =>
  list.find((item) => item.id === id)?.name ?? '';

/** Value taken from the minister's record: shown, not editable */
function ReadOnlyValue({ value }: { value: string }) {
  return <Input value={value || '—'} readOnly disabled className="disabled:cursor-default disabled:opacity-100" />;
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
